import React, { useCallback } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { MuiDatePicker } from "./muiDatepicker";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { Base } from "../../framework/base";

interface CompactDatePickerProps {
    label?: string;
    value: Date | null;
    onChange: (val: Date | null) => void;
    onMonthChange?: (date: Date) => void;
    highlightDates?: Date[];
    /** When true, arrows navigate to prev/next highlighted date and non-highlighted dates are unselectable in the calendar. */
    navigateThroughHighlightedDates?: boolean;
    /** When provided, overrides the text shown in the input field (e.g. shift start–end time). */
    displayLabel?: string;
    sx?: React.CSSProperties;
}

const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

const CompactDatePicker: React.FC<CompactDatePickerProps> = ({
    label,
    value,
    onChange,
    onMonthChange,
    highlightDates,
    navigateThroughHighlightedDates,
    displayLabel,
    sx,
}) => {
    const sortedHighlightDates = React.useMemo(() => {
        if (!highlightDates?.length) return [];
        return [...highlightDates].sort((a, b) => a.getTime() - b.getTime());
    }, [highlightDates]);

    const prevHighlightDate = React.useMemo(() => {
        if (!value || !sortedHighlightDates.length) return null;
        const valueStart = Base.dayjsToMinuteAccuracy(value).startOf("day").toDate();
        return (
            [...sortedHighlightDates]
                .reverse()
                .find((d) => d < valueStart) ?? null
        );
    }, [value, sortedHighlightDates]);

    const nextHighlightDate = React.useMemo(() => {
        if (!value || !sortedHighlightDates.length) return null;
        const valueEnd = Base.dayjsToMinuteAccuracy(value).endOf("day").toDate();
        return sortedHighlightDates.find((d) => d > valueEnd) ?? null;
    }, [value, sortedHighlightDates]);

    const handlePrevDay = useCallback(() => {
        if (!value) return;
        if (navigateThroughHighlightedDates) {
            if (prevHighlightDate) onChange(prevHighlightDate);
        } else {
            onChange(Base.dayjsToMinuteAccuracy(value).subtract(1, "day").toDate());
        }
    }, [value, navigateThroughHighlightedDates, prevHighlightDate, onChange]);

    const handleNextDay = useCallback(() => {
        if (!value) return;
        if (navigateThroughHighlightedDates) {
            if (nextHighlightDate) onChange(nextHighlightDate);
        } else {
            onChange(Base.dayjsToMinuteAccuracy(value).add(1, "day").toDate());
        }
    }, [value, navigateThroughHighlightedDates, nextHighlightDate, onChange]);

    const filterDate = useCallback(
        (date: Date) => sortedHighlightDates.some((h) => isSameDay(h, date)),
        [sortedHighlightDates]
    );

    return (
        <Box display="flex" alignItems="center" sx={{ ...sx }}>
            <MuiDatePicker
                label={label}
                value={value}
                onChange={onChange}
                onMonthChange={onMonthChange}
                highlightDates={highlightDates}
                filterDate={navigateThroughHighlightedDates ? filterDate : undefined}
                extraProps={{ small: true, displayValue: displayLabel }}
            />
            <IconButton
                onClick={handlePrevDay}
                disabled={navigateThroughHighlightedDates && !prevHighlightDate}
            >
                <ArrowBack />
            </IconButton>
            <IconButton
                onClick={handleNextDay}
                disabled={navigateThroughHighlightedDates && !nextHighlightDate}
            >
                <ArrowForward />
            </IconButton>
        </Box>
    );
};

export default CompactDatePicker;
